import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn, focusRingInset } from './cn';
import { Icon } from './base/Icon';
import { Skeleton } from './base/Skeleton';
import type { Align, Column, Density, SortState } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { usePersistentState } from './usePersistentState';

/**
 * DataTable — ตารางหลักของหลังบ้าน ใช้ในทุกหน้าที่มีรายการ
 *
 * รองรับ: เรียงคอลัมน์ · เลือกหลายแถว (คลิก, Shift+คลิก, เลือกทั้งหน้า) ·
 * ปักหมุดคอลัมน์ชิดซ้าย · ปรับความกว้างคอลัมน์ (ลากขอบ และคีย์บอร์ดด้วยลูกศร) ·
 * เดินด้วยคีย์บอร์ด J/K/ลูกศร/Enter · virtualization สำหรับ 10,000 แถว
 *
 * 3 สถานะที่ต้องมี:
 *   loading — skeleton ที่คง "จำนวนแถวเดิม" ไว้ (rowCountHint) เพื่อไม่ให้หน้าเด้ง
 *   empty   — ไม่มีข้อมูล (แยกกรณี "ไม่มีเลย" กับ "ตัวกรองไม่เจอ" ด้วย emptyState ที่ส่งเข้ามา)
 *   error   — เกิดข้อผิดพลาด พร้อมปุ่มลองใหม่
 *
 * ความสูงแถวมาจาก --row-height ที่ผูกกับ data-density และจำค่าที่ผู้ใช้เลือกไว้
 */

export interface DataTableProps<Row> {
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row) => string;
  /** ข้อความอธิบายตารางสำหรับ screen reader — บังคับ */
  caption: string;

  state?: 'ready' | 'loading' | 'error';
  /** จำนวนแถว skeleton ระหว่างโหลด — ควรส่งจำนวนแถวของหน้าก่อนหน้าเพื่อไม่ให้ความสูงเปลี่ยน */
  rowCountHint?: number;
  errorMessage?: string;
  onRetry?: () => void;
  /** แสดงเมื่อ rows ว่างและ state = ready */
  emptyState?: React.ReactNode;

  selectable?: boolean;
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;

  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;

  /** แถวที่คีย์บอร์ดโฟกัสอยู่ (uncontrolled ได้ถ้าไม่ส่ง) */
  focusedKey?: string | null;
  onFocusedKeyChange?: (key: string | null) => void;
  onOpenRow?: (row: Row) => void;
  /** แถวที่กำลังเปิดอยู่ใน SplitView — แสดงพื้นเลือกอยู่ */
  activeKey?: string | null;

  /** id ของคอลัมน์ที่แสดง เรียงตามลำดับที่ต้องการ (จาก ColumnPicker) */
  visibleColumnIds?: string[];
  /** คีย์สำหรับจำความกว้างคอลัมน์ต่อผู้ใช้ เช่น 'claims-queue' */
  persistKey?: string;
  density?: Density;
  /** ทำเครื่องหมายแถวที่ระบบจับว่าซ้ำ (FR-ADM-12) — ต้องมาพร้อมไอคอนในเซลล์ ไม่ใช่สีเดียว */
  isFlagged?: (row: Row) => boolean;
  /** ปิด virtualization (ใช้ในการทดสอบและตอนพิมพ์) */
  virtualize?: boolean;
  className?: string;
}

const alignClass: Record<Align, string> = { left: 'text-left', right: 'text-right', center: 'text-center' };
const OVERSCAN = 8;

export function DataTable<Row>(props: DataTableProps<Row>) {
  const {
    columns, rows, rowKey, caption,
    state = 'ready', rowCountHint = 12, errorMessage, onRetry, emptyState,
    selectable = false, selectedKeys = [], onSelectionChange,
    sort = null, onSortChange,
    focusedKey, onFocusedKeyChange, onOpenRow, activeKey = null,
    visibleColumnIds, persistKey, density = 'normal',
    isFlagged, virtualize = true, className,
  } = props;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewportH, setViewportH] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [rowH, setRowH] = useState(40);
  const lastClickedIndex = useRef<number | null>(null);

  const [widths, setWidths] = usePersistentState<Record<string, number>>(
    `cols.${persistKey ?? 'default'}`, {},
  );

  const shown = useMemo(() => {
    if (!visibleColumnIds) return columns;
    const byId = new Map(columns.map((c) => [c.id, c]));
    return visibleColumnIds.map((id) => byId.get(id)).filter(Boolean) as Array<Column<Row>>;
  }, [columns, visibleColumnIds]);

  const widthOf = useCallback(
    (c: Column<Row>) => widths[c.id] ?? c.width ?? 160,
    [widths],
  );

  /**
   * ตำแหน่งซ้ายของคอลัมน์ที่ปักหมุด — **วัดจาก DOM ไม่ใช่คำนวณจากค่าที่ประกาศ**
   *
   * 🔴 เดิมคำนวณเป็น `widthOf(c) + pad` โดย pad เป็นค่าเดาตามความหนาแน่น (16/24/32)
   *    ซึ่งผิด เพราะเบราว์เซอร์ยืดคอลัมน์ให้เต็มความกว้างตารางเมื่อผลรวมที่ประกาศ
   *    น้อยกว่าพื้นที่จริง ความกว้างจริงจึงไม่เท่าค่าที่ประกาศเลย
   *
   *    ผลคือคอลัมน์ปักหมุดเลื่อนไปทางขวาเกินตำแหน่งของตัวเอง แล้ว**ทับคอลัมน์ถัดไป**
   *    ซึ่งมี z-index ต่ำกว่า ตัวอักษรตัวแรกของคอลัมน์นั้นจึงหายไปใต้เงา
   *    (เจอจริงในคิวคำขอ: "รหัสคำขอ" จบที่ 329px แต่ "สมาชิก" เริ่มที่ 319px ทับกัน 10px)
   *
   *    ทีนี้วัดความกว้างจริงของหัวตารางหลัง layout เสร็จ แล้วบวกสะสม
   *    จึงถูกต้องทุกความหนาแน่น ทุกความกว้างหน้าจอ และทุกครั้งที่ผู้ใช้ย่อขยายคอลัมน์
   */
  const headCellRefs = useRef(new Map<string, HTMLTableCellElement>());
  const selectCellRef = useRef<HTMLTableCellElement | null>(null);
  const [pinnedOffsets, setPinnedOffsets] = useState<Record<string, number>>({});

  useLayoutEffect(() => {
    const measure = () => {
      const next: Record<string, number> = {};
      let x = selectCellRef.current?.getBoundingClientRect().width ?? 0;
      for (const c of shown) {
        if (!c.pinned) break;
        next[c.id] = x;
        const el = headCellRefs.current.get(c.id);
        x += el ? el.getBoundingClientRect().width : widthOf(c);
      }
      /* เทียบก่อนค่อย set — ไม่งั้น ResizeObserver จะวนไม่รู้จบ */
      setPinnedOffsets((prev) => {
        const keys = Object.keys(next);
        const same = keys.length === Object.keys(prev).length
          && keys.every((k) => Math.abs((prev[k] ?? -1) - next[k]!) < 0.5);
        return same ? prev : next;
      });
    };

    measure();

    const ro = new ResizeObserver(measure);
    if (scrollRef.current) ro.observe(scrollRef.current);
    if (selectCellRef.current) ro.observe(selectCellRef.current);
    for (const el of headCellRefs.current.values()) ro.observe(el);
    return () => ro.disconnect();
  }, [shown, selectable, density, widthOf]);

  const lastPinnedId = useMemo(() => {
    const pinned = shown.filter((c) => c.pinned);
    return pinned.length ? pinned[pinned.length - 1]!.id : null;
  }, [shown]);

  /* ---------- วัดความสูงแถวจริงจาก CSS variable (ไม่ hardcode) ---------- */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const read = () => {
      const v = getComputedStyle(el).getPropertyValue('--row-height').trim();
      const n = parseFloat(v);
      if (!Number.isNaN(n) && n > 0) setRowH(n);
      setViewportH(el.clientHeight);
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [density]);

  const total = rows.length;
  const start = virtualize ? Math.max(0, Math.floor(scrollTop / rowH) - OVERSCAN) : 0;
  const visibleCount = virtualize
    ? Math.min(total - start, Math.ceil(viewportH / rowH) + OVERSCAN * 2)
    : total;
  const slice = useMemo(() => rows.slice(start, start + visibleCount), [rows, start, visibleCount]);
  const padTop = start * rowH;
  const padBottom = Math.max(0, (total - start - visibleCount) * rowH);

  /* ---------- โฟกัสแถวด้วยคีย์บอร์ด ---------- */
  const [innerFocus, setInnerFocus] = useState<string | null>(null);
  const currentFocus = focusedKey !== undefined ? focusedKey : innerFocus;
  const setFocus = useCallback(
    (key: string | null) => {
      if (onFocusedKeyChange) onFocusedKeyChange(key);
      if (focusedKey === undefined) setInnerFocus(key);
    },
    [onFocusedKeyChange, focusedKey],
  );

  const focusIndex = useMemo(
    () => (currentFocus == null ? -1 : rows.findIndex((r) => rowKey(r) === currentFocus)),
    [currentFocus, rows, rowKey],
  );

  /** เลื่อนแถวที่โฟกัสเข้ามาในมุมมองด้วยการคำนวณ scrollTop เอง
      (ห้ามใช้ scrollIntoView — ทำให้ทั้งหน้าเลื่อน) */
  const revealIndex = useCallback(
    (index: number) => {
      const el = scrollRef.current;
      if (!el) return;
      const top = index * rowH;
      const bottom = top + rowH;
      const headerH = 40;
      if (top < el.scrollTop) el.scrollTop = top;
      else if (bottom > el.scrollTop + el.clientHeight - headerH) {
        el.scrollTop = bottom - el.clientHeight + headerH;
      }
    },
    [rowH],
  );

  const move = useCallback(
    (delta: number) => {
      if (!rows.length) return;
      const next = focusIndex < 0 ? 0 : Math.min(rows.length - 1, Math.max(0, focusIndex + delta));
      setFocus(rowKey(rows[next]!));
      revealIndex(next);
    },
    [focusIndex, rows, rowKey, setFocus, revealIndex],
  );

  useKeyboardShortcuts([
    { key: 'j', description: 'เลื่อนไปรายการถัดไป', group: 'การเลื่อนรายการ', handler: () => move(1) },
    { key: 'ArrowDown', description: 'เลื่อนไปรายการถัดไป', group: 'การเลื่อนรายการ', handler: () => move(1) },
    { key: 'k', description: 'เลื่อนไปรายการก่อนหน้า', group: 'การเลื่อนรายการ', handler: () => move(-1) },
    { key: 'ArrowUp', description: 'เลื่อนไปรายการก่อนหน้า', group: 'การเลื่อนรายการ', handler: () => move(-1) },
    {
      key: 'Enter',
      description: 'เปิดรายละเอียดของรายการที่เลือก',
      group: 'การเลื่อนรายการ',
      handler: () => { const r = rows[focusIndex]; if (focusIndex >= 0 && onOpenRow && r) onOpenRow(r); },
    },
  ], state === 'ready');

  /* ---------- การเลือกแถว ---------- */
  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));

  const toggleRow = useCallback(
    (index: number, shiftKey: boolean) => {
      if (!onSelectionChange) return;
      const next = new Set(selected);
      const key = rowKey(rows[index]!);
      if (shiftKey && lastClickedIndex.current !== null) {
        const range = [lastClickedIndex.current, index].sort((x, y) => x - y);
        const a = range[0]!;
        const b = range[1]!;
        const turningOn = !selected.has(key);
        for (let i = a; i <= b; i += 1) {
          const k = rowKey(rows[i]!);
          if (turningOn) next.add(k);
          else next.delete(k);
        }
      } else if (next.has(key)) next.delete(key);
      else next.add(key);
      lastClickedIndex.current = index;
      onSelectionChange([...next]);
    },
    [onSelectionChange, selected, rows, rowKey],
  );

  const toggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (allOnPageSelected) rows.forEach((r) => next.delete(rowKey(r)));
    else rows.forEach((r) => next.add(rowKey(r)));
    onSelectionChange([...next]);
  }, [onSelectionChange, selected, allOnPageSelected, rows, rowKey]);

  /* ---------- ปรับความกว้างคอลัมน์ ---------- */
  const dragState = useRef<{ id: string; startX: number; startW: number } | null>(null);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragState.current;
      if (!d) return;
      const col = shown.find((c) => c.id === d.id);
      const min = col?.minWidth ?? 72;
      setWidths((prev) => ({ ...prev, [d.id]: Math.max(min, d.startW + (e.clientX - d.startX)) }));
    };
    const onUp = () => { dragState.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [shown, setWidths]);

  const nudgeWidth = (c: Column<Row>, delta: number) =>
    setWidths((prev) => ({ ...prev, [c.id]: Math.max(c.minWidth ?? 72, widthOf(c) + delta) }));

  const sortBy = (c: Column<Row>) => {
    if (!c.sortable || !onSortChange) return;
    if (!sort || sort.columnId !== c.id) onSortChange({ columnId: c.id, direction: 'asc' });
    else if (sort.direction === 'asc') onSortChange({ columnId: c.id, direction: 'desc' });
    else onSortChange(null);
  };

  const cellPad = density === 'compact' ? 16 : density === 'comfortable' ? 32 : 24;
  const tableWidth = (selectable ? 40 : 0) + shown.reduce((sum, c) => sum + widthOf(c) + cellPad, 0);

  /* ---------- สถานะ error ---------- */
  if (state === 'error') {
    return (
      <div
        role="alert"
        className={cn(
          'flex flex-col items-center gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--error-border)] bg-[var(--error-bg)] px-[var(--space-6)] py-[var(--space-10)] text-center font-[family-name:var(--font-thai)]',
          className,
        )}
      >
        <Icon name="error" size={32} filled className="text-[var(--error-text)]" />
        <p className="text-[length:var(--text-form-lg)] leading-[var(--leading-thai)] text-[var(--error-text)]">
          {errorMessage ?? 'โหลดข้อมูลไม่สำเร็จ'}
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-white outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            ลองใหม่
          </button>
        )}
      </div>
    );
  }

  const showEmpty = state === 'ready' && rows.length === 0;

  return (
    <div
      data-density={density}
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)]',
        className,
      )}
    >
      <div
        ref={scrollRef}
        onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
        className="min-h-0 flex-1 overflow-auto"
      >
        <table
          role="grid"
          aria-rowcount={total}
          style={{ width: tableWidth, minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0 }}
          className="font-[family-name:var(--font-thai)] text-[length:var(--text-table)] leading-[var(--leading-table)]"
        >
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr style={{ height: 'var(--header-row-height)' }}>
              {selectable && (
                <th
                  ref={selectCellRef}
                  scope="col"
                  style={{ width: 40, left: 0 }}
                  className="sticky top-0 z-[var(--z-pinned-col)] bg-[var(--surface-100)] px-0 text-center align-middle shadow-[var(--elevation-sticky)]"
                >
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleAll}
                    aria-label={allOnPageSelected ? 'ยกเลิกการเลือกทุกแถวในหน้านี้' : 'เลือกทุกแถวในหน้านี้'}
                    className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                  />
                </th>
              )}
              {shown.map((c) => {
                const active = sort?.columnId === c.id;
                return (
                  <th
                    key={c.id}
                    ref={(el) => {
                      if (el) headCellRefs.current.set(c.id, el);
                      else headCellRefs.current.delete(c.id);
                    }}
                    scope="col"
                    aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                    style={{
                      /* width ใน Column หมายถึงความกว้างของ "เนื้อหา" ไม่ใช่ของกล่อง —
                         box-sizing เป็น border-box จึงต้องบวก --row-pad-x สองข้างกลับเข้าไป
                         ใช้ calc เพื่อให้ถูกต้องตามความหนาแน่นที่ผู้ใช้เลือก (8/12/16px) */
                      width: `calc(${widthOf(c)}px + var(--row-pad-x) * 2)`,
                      left: c.pinned ? pinnedOffsets[c.id] : undefined,
                      zIndex: c.pinned ? 'var(--z-pinned-col)' : undefined,
                      boxShadow: c.id === lastPinnedId ? 'var(--elevation-pinned)' : 'var(--elevation-sticky)',
                    }}
                    className={cn(
                      'sticky top-0 z-[var(--z-table-header)] bg-[var(--surface-100)] px-[var(--row-pad-x)] align-middle text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] text-[var(--ink-700)]',
                      c.pinned && 'sticky',
                      alignClass[c.align ?? (c.numeric ? 'right' : 'left')],
                    )}
                  >
                    <span className="flex items-center gap-1" style={{ justifyContent: c.numeric ? 'flex-end' : undefined }}>
                      {c.sortable ? (
                        <button
                          type="button"
                          onClick={() => sortBy(c)}
                          className={cn(
                            'inline-flex items-center gap-0.5 rounded-[var(--radius-sm)] px-1 py-0.5 hover:text-[var(--blue-700)]',
                            'outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1',
                          )}
                        >
                          <span className="bo-truncate">{c.header}</span>
                          <Icon
                            name={!active ? 'unfold_more' : sort!.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                            size={14}
                            className={active ? 'text-[var(--blue-700)]' : 'text-[var(--ink-400)]'}
                          />
                          <span className="sr-only">
                            {!active ? 'ยังไม่เรียงตามคอลัมน์นี้' : sort!.direction === 'asc' ? 'เรียงจากน้อยไปมาก' : 'เรียงจากมากไปน้อย'}
                          </span>
                        </button>
                      ) : (
                        <span className="bo-truncate px-1">{c.header}</span>
                      )}
                    </span>

                    {/* ตัวจับปรับความกว้าง — ใช้เมาส์ลาก หรือคีย์บอร์ดด้วยลูกศรซ้าย/ขวา */}
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`ปรับความกว้างคอลัมน์ ${c.header}`}
                      tabIndex={0}
                      onPointerDown={(e) => {
                        dragState.current = { id: c.id, startX: e.clientX, startW: widthOf(c) };
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowRight') { e.preventDefault(); nudgeWidth(c, 16); }
                        if (e.key === 'ArrowLeft') { e.preventDefault(); nudgeWidth(c, -16); }
                      }}
                      className="absolute right-0 top-0 h-full w-[6px] cursor-col-resize hover:bg-[var(--blue-200)] focus-visible:bg-[var(--blue-700)] focus-visible:outline-none"
                      style={{ position: 'absolute' }}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          {state === 'loading' ? (
            <tbody>
              {Array.from({ length: rowCountHint }).map((_, i) => (
                <tr key={i} style={{ height: 'var(--row-height)' }} aria-hidden>
                  {selectable && <td className="px-0" />}
                  {shown.map((c) => (
                    <td key={c.id} className="px-[var(--row-pad-x)]">
                      <Skeleton className="h-[14px] w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ) : showEmpty ? null : (
            <tbody>
              {padTop > 0 && <tr aria-hidden style={{ height: padTop }} />}
              {slice.map((row, i) => {
                const index = start + i;
                const key = rowKey(row);
                const isSelected = selected.has(key);
                const isFocused = currentFocus === key;
                const isActive = activeKey === key;
                const flagged = isFlagged?.(row) ?? false;
                return (
                  <tr
                    key={key}
                    aria-rowindex={index + 2}
                    aria-selected={selectable ? isSelected : undefined}
                    tabIndex={-1}
                    onClick={() => { setFocus(key); }}
                    onDoubleClick={() => onOpenRow?.(row)}
                    style={{ height: 'var(--row-height)' }}
                    className={cn(
                      'cursor-default border-b border-[var(--row-border)] transition-colors duration-[var(--duration-instant)]',
                      index % 2 === 1 ? 'bg-[var(--row-bg-alt)]' : 'bg-[var(--row-bg)]',
                      flagged && 'bg-[var(--row-bg-flagged)]',
                      isSelected && 'bg-[var(--row-bg-selected)]',
                      isActive && 'bg-[var(--row-bg-selected)]',
                      !isSelected && !isActive && 'hover:bg-[var(--row-bg-hover)]',
                      isFocused && 'bo-row-focus',
                    )}
                  >
                    {selectable && (
                      <td style={{ left: 0 }} className="sticky z-[var(--z-pinned-col)] bg-inherit px-0 text-center align-middle">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onClick={(e) => { e.stopPropagation(); toggleRow(index, (e as unknown as MouseEvent).shiftKey); }}
                          onChange={() => undefined}
                          aria-label={`เลือกแถวที่ ${index + 1}`}
                          className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                        />
                      </td>
                    )}
                    {shown.map((c) => (
                      <td
                        key={c.id}
                        style={{
                          left: c.pinned ? pinnedOffsets[c.id] : undefined,
                          zIndex: c.pinned ? 'var(--z-pinned-col)' : undefined,
                          boxShadow: c.id === lastPinnedId ? 'var(--elevation-pinned)' : undefined,
                        }}
                        className={cn(
                          'px-[var(--row-pad-x)] align-middle text-[var(--ink-900)]',
                          c.pinned && 'sticky bg-inherit',
                          c.numeric && 'bo-num',
                          alignClass[c.align ?? (c.numeric ? 'right' : 'left')],
                          focusRingInset,
                        )}
                      >
                        <div className="bo-truncate">{c.cell ? c.cell(row) : (c.value?.(row) ?? '—')}</div>
                      </td>
                    ))}
                  </tr>
                );
              })}
              {padBottom > 0 && <tr aria-hidden style={{ height: padBottom }} />}
            </tbody>
          )}
        </table>

        {showEmpty && <div className="p-[var(--space-10)]">{emptyState}</div>}
      </div>

      {/* ประกาศจำนวนแถวให้ screen reader ทราบเมื่อข้อมูลเปลี่ยน */}
      <p aria-live="polite" className="sr-only">
        {state === 'loading' ? 'กำลังโหลดข้อมูล' : `แสดง ${total} รายการ`}
      </p>
    </div>
  );
}
